import { deviationEnforced } from './adminHelpBehavior';

// Opt-in, short form hints only. Obvious fields and read-only views stay quiet.
const fraction = 'Fraction: 0.001 = 0.1%; 0 = no fee.';
const storedOnly = 'Stored only; not enforced by the current backend.';
export const pairHints = {
  price_source: 'Custom uses the price below; External uses the market feed.',
  custom_price: 'Quote units per base unit, e.g. 100 USDT per BTC1.',
  deviation: deviationEnforced
    ? '0.01 = 1%; 0 skips the check. Rejects orders outside the reference-price band.'
    : '0.01 = 1%; 0 skips the check. Not enforced on Swap21.',
  min_order_size: 'Base units. Display metadata only; not enforced.',
  min_base_amount_increment: 'Base-amount step. Display metadata only; not enforced.',
  min_price_increment: 'Quote-price step. Display metadata only; not enforced.',
  precisions: 'Grouping steps, e.g. 0.01, 0.1, 1. Larger values group more prices.',
  is_autoorders_enabled: 'Automatic/OTC orders, not customer matching.',
  enable_alerts: 'Missing external-price alerts.',
};
const feeHints = {
  limits_deposit_min: 'Asset units. Smaller deposits may not be credited automatically.',
  limits_deposit_max: 'Asset units. 0 is NOT unlimited.',
  limits_withdrawal_min: 'Asset units; 0 removes this minimum.',
  limits_withdrawal_max: 'Asset units; 0 removes this cap. Monthly limits still apply.',
  limits_order_min: 'Base units; applies when backend order-limit checks are enabled.',
  limits_order_max: 'Spent-asset units. 0 blocks positive amounts when order-limit checks are enabled.',
  limits_code_max: 'Legacy code limit; no active use found.',
  limits_accumulation_min: 'Asset units required before funds are eligible for collection.',
  limits_keeper_accumulation_balance: 'Asset-unit threshold for keeper versus cold-wallet routing.',
  limits_accumulation_max_gas_price: 'Gwei. At or above the ceiling, collection waits; 0 removes the ceiling.',
  fee_deposit_address: 'Fixed amount deducted in the deposited asset, not a percentage.',
  fee_deposit_code: 'Legacy code fee; no active use found.',
  fee_withdrawal_code: 'Legacy code fee; use Network fees for blockchain withdrawals.',
  fee_order_limits: fraction,
  fee_order_market: 'Fraction: 0.001 = 0.1%. The current fee resolver uses the limit-order fee instead.',
  fee_exchange_value: fraction,
  address_fee: 'Fixed withdrawal deduction in the selected asset, not a percentage.',
  fee_rate: fraction,
};
const resourceHints = {
  'core/pairsettings': pairHints,
  'core/feesandlimits': feeHints,
  'core/withdrawallimitlevel': { amount: 'USDT-equivalent per calendar month. 0 is NOT unlimited.', level: 'Tier 0 is the default; its configured amount still applies.' },
  'core/withdrawaluserlimit': { limit: 'Select a withdrawal tier, not an amount.' },
  'core/userfee': { fee_rate: fraction },
  'core/userexchangefee': { fee_rate: fraction },
  'core/withdrawalfee': { address_fee: feeHints.address_fee },
  'core/profile': { auto_logout_timeout: 'Inactivity timeout in minutes for expiring-token sessions.', payouts_freezed_till: 'Payouts remain frozen until this UTC time.' },
  'core/userkyc': { forced_approve: 'Overrides the normal KYC decision; requires an authorized review.' },
  'core/quickswapsettings': {
    default_fee_percentage: 'Percent: 1 = 1%; 0 = no fee.',
    min_fee_percentage: 'Percent. Keep minimum ≤ default ≤ maximum.',
    max_fee_percentage: 'Percent. Keep minimum ≤ default ≤ maximum.',
    min_swap_amount_usd: 'USD. Currently not enforced by the swap service.',
    max_swap_amount_usd: 'USD. Currently not enforced by the swap service.',
    require_kyc: storedOnly,
    supported_currencies: 'JSON symbols, e.g. ["BTC1","USDT"].',
    liquidity_provider_enabled: storedOnly,
    liquidity_reserve_btc1: 'BTC1 units. Does not enforce or fund a reserve.',
  },
  'bots/botconfig': {
    loop_period: 'Seconds between runs when random timing is off.',
    min_period: 'Shortest random delay, in seconds.', max_period: 'Longest random delay, in seconds.',
    ext_price_delta: 'Reference-price band: 0.001 = 0.1%.',
    symbol_precision: 'Decimal places for base-asset quantity.', quote_precision: 'Decimal places for the order price.',
    min_order_quantity: 'Base-asset units.', max_order_quantity: 'Base-asset units.',
    match_user_orders: 'Can execute real trades against customer orders.', instant_match: 'Can place immediately matching orders.',
    custom_price: 'Bot-only reference, in quote units per base unit.', ohlc_period: 'Strategy interval, in minutes.',
    low_orders_match: 'Places counter-orders when spread and size conditions match.',
    low_orders_max_match_size: 'Backend order-amount threshold for low-spread matching.',
    low_orders_spread_size: 'Ask-minus-bid gap in quote units, not percent.',
    low_orders_min_order_size: 'Minimum counter-order amount; may exceed the matched order.',
    low_orders_match_greater_order: 'Targets the larger eligible order instead of the smaller one.',
  },
  'cryptocoins/scoringsettings': {
    min_score: 'Scores below this are rejected; higher is stricter.',
    deffered_scoring_time: 'Wait time before deferred scoring, in minutes.',
    min_tx_amount: 'Asset-unit threshold for the small-amount scoring result.',
  },
  'p2p/p2psettings': {
    max_active_ads_per_user: storedOnly, max_active_orders_per_user: storedOnly,
    max_payment_time_limit_minutes: 'Minutes. The current ad validator separately allows 15–360.',
    maker_fee_percentage: storedOnly, taker_fee_percentage: storedOnly,
    min_trade_amount_usd: 'USD. No active enforcement found.', max_trade_amount_usd: 'USD. No active enforcement found.',
    large_trade_threshold_usd: 'USD threshold for the P2P compliance KYC check.',
    auto_release_after_hours: 'Hours after confirmation; the auto-release job can release eligible escrow.',
    dispute_escalation_hours: storedOnly,
  },
  'p2p/p2puserprofile': { daily_trade_limit: 'Daily compliance threshold; mixed-fiat totals are not reliably USD-converted.' },
  'p2p/p2ppaymentmethod': { supported_currencies: 'JSON fiat codes, e.g. ["USD","EUR"].' },
  'notifications/notification': { target_users: 'Changing this replaces recipients. Verified means email-verified, not KYC.' },
  funding: { password: 'Exchange master password, not the customer’s login password.' },
  liquidity: { type: 'Liquidity and Deposits are separate accounting buckets.' },
  'operations/create-withdrawal-tier': { amount: 'USDT-equivalent per calendar month. 0 is NOT unlimited.' },
  'operations/set-accumulation-limit': { limit: feeHints.limits_keeper_accumulation_balance },
  'operations/recheck-deposits': { user_id: 'Leave blank to include all eligible customers.' },
  'operations/withdrawal-inspection': { state: 'Use the withdrawal state code; leave blank for all states.' },
};

export function getFieldHelp(resource = '', name, field = {}, context = {}, filter = false) {
  if (filter || field.attributes?.read_only || field.help === false) return '';
  if (resource === 'core/settings' && name === 'value') return context.name === 'alert_on_external_pair_price'
    ? 'Legacy setting, no longer read. Use the pair’s alert switch.'
    : 'Confirm this setting’s backend format before editing.';
  if (['core/disabledcoin', 'core/userrestrictions'].includes(resource) && name?.startsWith('disable_')) return 'Yes = blocked; No = allowed.';
  const key = { auto_orders_enabled: 'is_autoorders_enabled' }[name] || name;
  if (resource === 'core/pairsettings' && name === 'precisions' && field.type === 'list') return 'Edit under Assets & pairs → Configure; that form converts grouping steps to an array.';
  return resourceHints[resource]?.[key] || '';
}
